import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API, useAuth } from "../context/AuthContext";
import { Heart, Check, Loader2, ShieldCheck, ArrowRight } from "lucide-react";

const TIERS = [
  {
    lookupKey: "membership_year_10",
    name: "Member",
    price: "£10",
    accent: "#FFD93D",
    text: "#0A0A0A",
    blurb: "The full membership — access the professional network and back ND filmmakers for a year.",
  },
  {
    lookupKey: "membership_year_20",
    name: "Supporter",
    price: "£20",
    accent: "#FF2E63",
    text: "#FFF8E7",
    blurb: "Everything in Member, plus you fund one submission fee waiver for an ND artist.",
    featured: true,
  },
  {
    lookupKey: "membership_year_50",
    name: "Champion",
    price: "£50",
    accent: "#1A5DFF",
    text: "#FFF8E7",
    blurb: "Everything in Member, plus you contribute directly to artist honoraria and festival travel.",
  },
];

const PERKS = [
  "Access to the vetted professional network directory",
  "Priority invites to screenings and launch nights",
  "Your fee goes to honoraria, fee waivers and artist travel",
  "Cancel any time — no questions asked",
];

export default function Membership() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [membership, setMembership] = useState(null);

  useEffect(() => {
    if (!user) { setMembership(null); return; }
    (async () => {
      try {
        const r = await axios.get(`${API}/membership/me`, { withCredentials: true });
        setMembership(r.data);
      } catch { setMembership(null); }
    })();
  }, [user]);

  const startCheckout = async (lookupKey, requiresAuth) => {
    setError(null);
    if (requiresAuth && !user) {
      navigate("/signin?next=/membership");
      return;
    }
    setBusy(lookupKey);
    try {
      const r = await axios.post(
        `${API}/payments/checkout`,
        { lookup_key: lookupKey, origin_url: window.location.origin },
        { withCredentials: true }
      );
      window.location.href = r.data.checkout_url;
    } catch (e) {
      setError("Could not start checkout. Please try again.");
      setBusy(null);
    }
  };

  return (
    <div className="bg-[#FFF8E7]" data-testid="page-membership">
      {/* HERO */}
      <section className="relative overflow-hidden border-b-2 border-[#0A0A0A]" data-testid="membership-hero">
        <div className="absolute inset-0 halftone-pink opacity-[0.07]" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="inline-block bg-[#FF2E63] text-[#FFF8E7] px-3 py-1 text-xs uppercase tracking-[0.2em] font-bold mb-4 border-2 border-[#0A0A0A]">Support us</div>
          <h1 className="font-heading font-black text-[#0A0A0A] text-4xl sm:text-5xl lg:text-6xl xl:text-7xl tracking-tight leading-[0.95]">
            Back the festival.<br />
            <span className="text-[#1A5DFF] [text-shadow:3px_3px_0_#0A0A0A]">Pay the artists.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-[#0A0A0A] leading-relaxed">
            Annual membership starts at £10 — pay what you can. 100% of fees go to artist honoraria,
            submission fee waivers and festival travel. One-off donations welcome too.
          </p>
        </div>
      </section>

      {/* MEMBER STATUS */}
      {membership?.is_member && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10">
          <div className="bg-[#1A5DFF] text-[#FFF8E7] border-2 border-[#0A0A0A] pop-shadow p-5 flex items-center gap-3" data-testid="member-status-banner">
            <ShieldCheck className="w-6 h-6 shrink-0" />
            <div>
              <div className="font-heading font-bold text-lg">You&apos;re a member — thank you!</div>
              <div className="text-sm text-[#FFE8A8]">Your annual membership is active. You can still top it up or donate below.</div>
            </div>
          </div>
        </div>
      )}

      {/* MEMBERSHIP TIERS */}
      <section className="py-14 md:py-20" data-testid="membership-tiers">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#1A5DFF] mb-3">Annual membership</div>
          <h2 className="font-heading font-black text-3xl sm:text-4xl text-[#0A0A0A] tracking-tight mb-2">Pick what you can afford.</h2>
          <p className="text-[#2C2C2C] mb-4 max-w-2xl">Every tier gets the same membership — the higher amounts just fund more artists. Renews yearly, cancel any time.</p>
          {!user && !loading && (
            <p className="text-sm text-[#2C2C2C] mb-8" data-testid="membership-signin-note">
              You&apos;ll be asked to <button onClick={() => navigate("/signin?next=/membership")} className="underline underline-offset-2 font-bold text-[#FF2E63] hover:text-[#0A0A0A]" data-testid="membership-signin-inline">sign in with Google</button> first so we can link the membership to your account.
            </p>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mt-6">
            {TIERS.map((t) => (
              <article
                key={t.lookupKey}
                className={`bg-white border-2 border-[#0A0A0A] pop-shadow flex flex-col ${t.featured ? "md:-translate-y-3" : ""}`}
                data-testid={`tier-card-${t.lookupKey}`}
              >
                <div className="px-6 py-4 border-b-2 border-[#0A0A0A] flex items-center justify-between" style={{ backgroundColor: t.accent, color: t.text }}>
                  <span className="font-heading font-black text-xl tracking-tight">{t.name}</span>
                  {t.featured && <span className="text-[10px] uppercase tracking-[0.16em] font-bold bg-[#0A0A0A] text-[#FFF8E7] px-2 py-1">Most popular</span>}
                </div>
                <div className="p-6 flex flex-col gap-4 flex-1">
                  <div className="flex items-end gap-2">
                    <span className="font-heading font-black text-5xl text-[#0A0A0A]">{t.price}</span>
                    <span className="text-sm text-[#2C2C2C] mb-2">/ year</span>
                  </div>
                  <p className="text-sm text-[#2C2C2C] leading-relaxed flex-1">{t.blurb}</p>
                  <button
                    onClick={() => startCheckout(t.lookupKey, true)}
                    disabled={busy !== null}
                    data-testid={`tier-join-${t.lookupKey}`}
                    className="w-full bg-[#FF2E63] text-[#FFF8E7] hover:bg-[#E0204F] disabled:opacity-60 border-2 border-[#0A0A0A] rounded-none px-6 py-3.5 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#FF2E63] inline-flex items-center justify-center gap-2"
                  >
                    {busy === t.lookupKey ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                    {user ? `Join at ${t.price}/year` : "Sign in to join"}
                  </button>
                </div>
              </article>
            ))}
          </div>

          <ul className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-3xl" data-testid="membership-perks">
            {PERKS.map((p) => (
              <li key={p} className="flex items-start gap-2 text-sm text-[#0A0A0A]">
                <Check className="w-4 h-4 text-[#1A5DFF] mt-0.5 shrink-0" /> {p}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* DONATIONS */}
      <section className="py-14 md:py-20 border-t-2 border-[#0A0A0A] relative" data-testid="donation-section">
        <div className="absolute inset-0 halftone-pink opacity-[0.05]" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#0A0A0A] text-[#FFF8E7] border-2 border-[#0A0A0A] p-10 md:p-16 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
            <div className="md:col-span-8">
              <div className="inline-block bg-[#FFD93D] text-[#0A0A0A] px-3 py-1 text-xs uppercase tracking-[0.2em] font-bold mb-4 border-2 border-[#FFD93D]">One-off donation</div>
              <h2 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1]">
                Not ready for membership?<br />
                <span className="text-[#FF2E63] [text-shadow:3px_3px_0_#FFD93D]">Give any amount.</span>
              </h2>
              <p className="mt-5 text-[#FFE8A8] leading-relaxed max-w-xl">
                No account needed. You choose the amount on the secure Stripe checkout page — every pound goes to film productions and keeping the festival free to watch.
              </p>
            </div>
            <div className="md:col-span-4 flex md:justify-end">
              <button
                onClick={() => startCheckout("donation_open", false)}
                disabled={busy !== null}
                data-testid="donate-btn"
                className="bg-[#FFD93D] text-[#0A0A0A] hover:bg-[#FFCB05] disabled:opacity-60 border-2 border-[#FF2E63] pop-shadow rounded-none px-10 py-5 text-base font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#FFD93D] inline-flex items-center gap-2"
              >
                {busy === "donation_open" ? <Loader2 className="w-5 h-5 animate-spin" /> : <Heart className="w-5 h-5" />}
                Donate now
              </button>
            </div>
          </div>
          {error && <div className="mt-6 text-[#FF2E63] font-bold" data-testid="checkout-error">{error}</div>}
          <p className="mt-6 text-xs text-[#2C2C2C]" data-testid="payment-security-note">
            Payments are processed securely by Stripe. We never see or store your card details.
          </p>
        </div>
      </section>
    </div>
  );
}
